// src/components/RegisterProfessional.jsx
import React, { useMemo, useState } from 'react';
import { Container, Row, Col, Form, Button, ProgressBar, Alert } from 'react-bootstrap';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import './RegisterProfessional.css';

const API_URL = 'https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod';

const LANGUAGES = ['Français', 'English', 'Espagnol', 'Hindi', 'Creole', 'Filipino', 'Italian', 'Russe', 'Arabe', 'Roumain'];
// juste après LANGUAGES ...
const LANGUAGE_LEVELS = ['Débutant','Intermédiaire','Avancé','Courant','Bilingue'];

const CULTURES = [
  'Africaine','Asiatique','Canadienne','Caribéenne','Européenne','Europe de l’Est',
  'Indienne','Italienne','Latine','Maghrébine','Mexicaine','Premières Nations',
  'Portugaise','Russe'
];

/* ------------------------ Schemas utils ------------------------ */

// Email strict
const emailStrict = Yup.string()
  .trim()
  .email('Courriel invalide')
  .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, 'Courriel invalide')
  .required('Courriel requis');

// Téléphone selon indicatif
const phoneByCountry = Yup.string()
  .required('Numéro requis')
  .test('phone-by-country', 'Numéro invalide', function (value) {
    const code = String(this.parent.countryCode || '');
    const digits = String(value || '').replace(/\D/g, '');
    if (code.startsWith('+1'))   return digits.length === 10;                     // CA/US
    if (code.startsWith('+33'))  return digits.length === 9;                      // FR
    if (code.startsWith('+221')) return digits.length === 9;                      // SN
    if (code.startsWith('+44'))  return digits.length >= 9 && digits.length <= 10; // UK
    return digits.length >= 7 && digits.length <= 15;
  });

const phoneRegex = /^[+()\-.\s\d]{7,20}$/;
const urlRegex   = /^(https?:\/\/)?([^\s.]+\.[^\s]{2,}|localhost(:\d{2,5})?)(\/\S*)?$/i;

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_MIME = ['application/pdf','image/png','image/jpeg'];
const singleFileSchema = Yup.mixed()
  .test('size', 'Fichier ≤ 5 Mo', v => !v || (v.size <= MAX_FILE_SIZE))
  .test('type', 'Types acceptés: PDF / JPG / PNG', v => !v || ACCEPTED_MIME.includes(v.type));

const passwordSchema = Yup.string()
  .min(8, 'Au moins 8 caractères')
  .matches(/[a-z]/, '1 minuscule')
  .matches(/[A-Z]/, '1 majuscule')
  .matches(/\d/, '1 chiffre')
  .required('Mot de passe requis');

// NEQ (10 chiffres) ou "N/A" (seulement requis si hasCompany = true)
const neqSchemaBase = Yup.string().test(
  'neq-or-na',
  'NEQ invalide (10 chiffres) ou "N/A"',
  v => {
    if (!v && v !== '') return false;
    const val = String(v || '').trim().toUpperCase();
    if (val === '') return true;                // la règle required conditionnelle gère l’obligation
    if (val === 'NA' || val === 'N/A') return true;
    return /^\d{10}$/.test(val);
  }
);

// Fichiers
const filesSchema = Yup.mixed()
  .required('Pièces justificatives requises (CV, licence, preuve de formation, etc.)')
  .test('at-least-one', 'Ajoute au moins 1 fichier', v => v && v.length > 0)
  .test('file-size', 'Chaque fichier doit faire ≤ 5 Mo', v => !v ? false : Array.from(v).every(f => f.size <= MAX_FILE_SIZE))
  .test('file-type', 'Types acceptés: PDF / JPG / PNG', v => !v ? false : Array.from(v).every(f => ACCEPTED_MIME.includes(f.type)));

const asNullable = s => s.transform(v => (v === '' ? null : v)).nullable().notRequired();

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');


/* ------------------------ Validations par étape ------------------------ */
const stepSchemas = [
  // Étape 1
  Yup.object({
    firstName  : Yup.string().trim().required('Prénom requis'),
    lastName   : Yup.string().trim().required('Nom requis'),
    email      : emailStrict,
    password   : passwordSchema,
    countryCode: Yup.string().required('Indicatif requis'),
    phone      : phoneByCountry,
    sex        : Yup.string().oneOf(['Masculin','Feminin'], 'Genre requis').required('Genre requis'),
    profession : Yup.string().trim().required('Profession requise'),

    // Entreprise → requis uniquement si coché
    hasCompany    : Yup.boolean().default(false),
    // Étape 1 (extrait)
companyName: Yup.string()
.trim()
.when('hasCompany', {
  is: true,
  then: s => s.required("Nom de l'entreprise requis"),
  otherwise: s => s.strip(),           // <- retire du schéma si non coché
}),

NEQ: Yup.string()
.test('neq-or-na', 'NEQ invalide (10 chiffres) ou "N/A"', v => {
  const val = String(v || '').trim().toUpperCase();
  if (val === '') return true;
  if (val === 'NA' || val === 'N/A') return true;
  return /^\d{10}$/.test(val);
})
.when('hasCompany', {
  is: true,
  then: s => s.required('NEQ requis (10 chiffres) ou "N/A"'),
  otherwise: s => s.strip(),           // <- retire du schéma si non coché
}),

companyWebsite: Yup.string()
.matches(/^(https?:\/\/)?([^\s.]+\.[^\s]{2,}|localhost(:\d{2,5})?)(\/\S*)?$/i, 'URL invalide')
.transform(v => (v === '' ? null : v))
.nullable()
.when('hasCompany', {
  is: true,
  then: s => s,                        // optionnel même si coché (laisser vide OK)
  otherwise: s => s.strip(),           // <- retire du schéma si non coché
}),

    // Autre personne contact → visible si coché, mais jamais obligatoire
    hasContact   : Yup.boolean().default(false),
    contactPerson: asNullable(Yup.string().trim()),
    contactPhone : asNullable(Yup.string().matches(phoneRegex, 'Téléphone invalide')),
    proEmail     : asNullable(Yup.string().email('Courriel professionnel invalide')),
  }),

  // Étape 2
  Yup.object({
    servicesDescription: Yup.string().trim().min(10,'Décrivez vos services (≥ 10 caractères)').required('Description requise'),
    servicedCities     : Yup.string().trim().required('Villes couvertes requises'),
    fees               : Yup.string().trim().required('Frais requis (ex: 80$/h)'),
  }),

// Étape 3
Yup.object({
  // on lit la profession ici pour les règles conditionnelles
  profession: Yup.string(),

  yearsExperience: Yup.number()
    .typeError('Nombre invalide')
    .min(0,'≥ 0')
    .max(80,'…relax 😅')
    .required('Requis'),

  // ✅ REMETTRE la validation du champ multiple
  qualifications: filesSchema,   // ← redevient obligatoire pour tout le monde

  // Fichiers simples (facultatifs par défaut)
  licenseFile: singleFileSchema.nullable(),
  trainingProofFile: singleFileSchema.nullable(),

  heardAboutUs: Yup.string().trim().required('Champ requis'),

  languagesSelected: Yup.array().of(Yup.string()).min(1, 'Choisis au moins une langue'),
  languageLevels: Yup.object().test(
    'level-for-each',
    'Choisis un niveau pour chaque langue sélectionnée',
    function (levelsObj) {
      const selected = this.parent.languagesSelected || [];
      if (!selected.length) return true;
      return selected.every(lang => {
        const lvl = levelsObj?.[lang] || '';
        return LANGUAGE_LEVELS.includes(lvl);
      });
    }
  ),

  cultures: Yup.array().of(Yup.string()).nullable().notRequired(),
  otherComments: asNullable(Yup.string().trim()),
  birthDate: asNullable(Yup.date().max(new Date(), 'Date invalide')),
})
// 👉 Préposé : au moins 1 des deux fichiers requis (tolère sans accent/pluriels)
.test('prepose-license-or-training', '', function (values) {
  const prof = normalize(values?.profession);
  // on matche "prepose", "preposes", "preposée", etc.
  const isPrepose = /\bprepose/.test(prof);
  if (isPrepose) {
    if (values.licenseFile || values.trainingProofFile) return true;
    return this.createError({
      path: 'licenseFile',
      message: 'Pour les préposés, fournissez au moins une licence professionnelle ou une preuve de formation.',
    });
  }
  return true;
}),

  // Étape 4
  Yup.object({
    consent          : Yup.boolean().oneOf([true], 'Tu dois accepter les termes'),
    signatureFullName: Yup.string().trim().required('Signature (nom complet) requise'),
  }),
];

/* ------------------------ Valeurs initiales ------------------------ */
const initialValues = {
  // Étape 1
  firstName: '', lastName: '', email: '', password: '',
  countryCode: '+1 - Canada', phone: '', sex: '', profession: '',
  hasCompany: false,
  companyName: '', companyWebsite: '', NEQ: '',
  hasContact: false,
  contactPerson: '', contactPhone: '', proEmail: '',

  // Étape 2
  servicesDescription: '', servicedCities: '', fees: '',

  // Étape 3
yearsExperience: '',
otherComments: '',
heardAboutUs: '',
languagesSelected: [],       // ← nouvelles clés
languageLevels: {},          // ← { 'Français': 'Avancé', ... }
cultures: [],
// qualifications multiple (requis) + 2 nouveaux fichiers simples
qualifications: undefined,
licenseFile: null,
trainingProofFile: null,
birthDate: '',

  // Étape 4
  consent: false, signatureFullName: '',
};

/* =================================================================== */
export default function RegisterProfessional() {
  const [step, setStep] = useState(0);
  const stepsCount = 4;
  const progress = useMemo(() => Math.round(((step + 1) / stepsCount) * 100), [step]);

  const next = async (validateForm, setFieldTouched, schema) => {
    const errors = await validateForm();
    const stepFields = Object.keys(schema.fields || {});
    const hasStepErrors = stepFields.some(k => errors[k]);
    if (hasStepErrors) {
      stepFields.forEach(k => setFieldTouched(k, true, false));
      const firstKey = stepFields.find(k => errors[k]);
      if (firstKey) document.querySelector(`[name="${firstKey}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setStep(s => Math.min(s + 1, 3));
  };

  const back = () => setStep(s => Math.max(0, s - 1));

  const handleSubmit = async (values, { setSubmitting, validateForm, resetForm }) => {
    try {
      const errors = await validateForm();
      if (Object.keys(errors).length) { setSubmitting(false); return; }

      const fd = new FormData();
      const { qualifications, licenseFile, trainingProofFile, ...rest } = values;
      fd.append('payload', JSON.stringify(rest));
      
      if (qualifications && qualifications.length) {
        Array.from(qualifications).forEach(f => fd.append('qualifications', f, f.name));
      }
      if (licenseFile) {
        fd.append('licenseFile', licenseFile, licenseFile.name);
      }
      if (trainingProofFile) {
        fd.append('trainingProofFile', trainingProofFile, trainingProofFile.name);
      }
      const resp = await fetch(`${API_URL}/professionals/apply`, { method: 'POST', body: fd });
      let data = {}; try { data = await resp.json(); } catch {}
      if (!resp.ok) {
        if (resp.status === 413) throw new Error('Taille de fichier trop grande (413).');
        if (resp.status === 415) throw new Error('Type de fichier non supporté (415).');
        if (resp.status === 400) throw new Error(data?.error || 'Requête invalide (400).');
        throw new Error(data?.error || `Erreur serveur (HTTP ${resp.status}).`);
      }

      alert('✅ Inscription envoyée !');
      resetForm();
    } catch (err) {
      console.error('Submit error:', err);
      alert(`❌ Échec de l’envoi : ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid className="py-5 px-4">
      <div className="text-center mb-4">
        <div className="text-uppercase fw-semibold" style={{ letterSpacing: '1px', color: '#6c7485', fontSize: '0.9rem' }}>
          <span style={{ borderBottom: '3px solid #8c5cc2', paddingBottom: '3px' }}>PROFESSIONNELS</span>
        </div>
        <h1 className="fw-bold mb-3">Devenir <span className="text-primary">Prestataire</span> chez KnowMediQ</h1>
        <p className="mx-auto" style={{ maxWidth: '800px', whiteSpace: 'nowrap', color: '#6c757d' }}>
          4 étapes : Informations personnelles → Entreprise/Service → Informations additionnelles → Consentement & Signature.
        </p>
      </div>

      <Row className="align-items-start g-4">
        <Col md={6} className="d-flex justify-content-center">
          <div className="image-stack">
            <img src="/images/imageknowmediq1.png" alt="soin en personne" className="img-fluid" />
            <img src="/images/imageknowmediq2.webp" alt="soin en visio" className="img-fluid" />
          </div>
        </Col>

        <Col md={6}>
          <div className="border rounded p-4 shadow bg-white">
            <div className="mb-3">
              <ProgressBar now={progress} label={`${progress}%`} />
              <div className="small text-muted mt-1">Étape {step + 1} / {stepsCount}</div>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={stepSchemas[step]}
              validateOnBlur
              validateOnChange={false}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, setFieldValue, validateForm, setFieldTouched, isSubmitting }) => (
                <FormikForm noValidate encType="multipart/form-data">
                  {/* ====== ÉTAPE 1 : Infos personnelles ====== */}
                  {step === 0 && (
                    <>
                      <h5 className="mb-3">1. Informations personnelles</h5>

                      <Form.Group className="mb-3">
                        <Form.Label>Prénom *</Form.Label>
                        <Field as={Form.Control} name="firstName" isInvalid={touched.firstName && !!errors.firstName} />
                        <Form.Control.Feedback type="invalid"><ErrorMessage name="firstName" /></Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Nom de famille *</Form.Label>
                        <Field as={Form.Control} name="lastName" isInvalid={touched.lastName && !!errors.lastName} />
                        <Form.Control.Feedback type="invalid"><ErrorMessage name="lastName" /></Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Sexe *</Form.Label><br />
                        <Form.Check
                          inline type="radio" label="Masculin" value="Masculin" name="sex"
                          checked={values.sex === 'Masculin'} onChange={() => setFieldValue('sex', 'Masculin')}
                          isInvalid={touched.sex && !!errors.sex}
                        />
                        <Form.Check
                          inline type="radio" label="Feminin" value="Feminin" name="sex"
                          checked={values.sex === 'Feminin'} onChange={() => setFieldValue('sex', 'Feminin')}
                          isInvalid={touched.sex && !!errors.sex}
                        />
                        {touched.sex && errors.sex && <div className="invalid-feedback d-block">{errors.sex}</div>}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Profession *</Form.Label>
                        <Field as={Form.Control} name="profession" placeholder="Ex: Infirmier(ère)"
                          isInvalid={touched.profession && !!errors.profession} />
                        <Form.Control.Feedback type="invalid"><ErrorMessage name="profession" /></Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Courriel *</Form.Label>
                        <Field as={Form.Control} type="email" name="email" isInvalid={touched.email && !!errors.email} />
                        <Form.Control.Feedback type="invalid"><ErrorMessage name="email" /></Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Mot de passe *</Form.Label>
                        <Field as={Form.Control} type="password" name="password" isInvalid={touched.password && !!errors.password} />
                        <Form.Control.Feedback type="invalid"><ErrorMessage name="password" /></Form.Control.Feedback>
                        <div className="form-text">Min 8 caractères, 1 majuscule, 1 minuscule, 1 chiffre.</div>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Numéro de téléphone *</Form.Label>
                        <Row>
                          <Col md={4}>
                            <Field as={Form.Select} name="countryCode">
                              <option>+1 - Canada</option>
                              <option>+33 - France</option>
                              <option>+221 - Sénégal</option>
                              <option>+44 - UK</option>
                              <option>+49 - Allemagne</option>
                            </Field>
                          </Col>
                          <Col md={8}>
                            <Field
                              as={Form.Control}
                              type="tel"
                              name="phone"
                              placeholder="Saisissez votre numéro de téléphone"
                              isInvalid={touched.phone && !!errors.phone}
                            />
                            <Form.Control.Feedback type="invalid"><ErrorMessage name="phone" /></Form.Control.Feedback>
                          </Col>
                        </Row>
                      </Form.Group>

                      {/* Autre personne contact (optionnel) */}
                      <Form.Group className="mb-2">
                        <Form.Check
                          type="checkbox"
                          name="hasContact"
                          checked={values.hasContact}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFieldValue('hasContact', checked);
                            if (!checked) {
                              setFieldValue('contactPerson', '');
                              setFieldValue('contactPhone', '');
                              setFieldValue('proEmail', '');
                            }
                          }}
                          label="Autre personne contact"
                        />
                      </Form.Group>

                      {values.hasContact && (
                        <>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Nom du représentant / personne contact</Form.Label>
                                <Field as={Form.Control} name="contactPerson"
                                      isInvalid={touched.contactPerson && !!errors.contactPerson} />
                                <Form.Control.Feedback type="invalid"><ErrorMessage name="contactPerson" /></Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Numéro de téléphone (Personne Contact)</Form.Label>
                                <Field as={Form.Control} name="contactPhone"
                                      isInvalid={touched.contactPhone && !!errors.contactPhone} />
                                <Form.Control.Feedback type="invalid"><ErrorMessage name="contactPhone" /></Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Form.Group className="mb-3">
                            <Form.Label>Adresse e-mail personne contact</Form.Label>
                            <Field as={Form.Control} type="email" name="proEmail"
                                  isInvalid={touched.proEmail && !!errors.proEmail} />
                            <Form.Control.Feedback type="invalid"><ErrorMessage name="proEmail" /></Form.Control.Feedback>
                          </Form.Group>
                        </>
                      )}

                      {/* Entreprise (requis seulement si coché) */}
                      <Form.Group className="mb-2">
                                                      <Form.Check
                                  type="checkbox"
                                  name="hasCompany"
                                  checked={values.hasCompany}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setFieldValue('hasCompany', checked);

                                    if (!checked) {
                                      // Nettoie valeurs
                                      setFieldValue('companyName', '');
                                      setFieldValue('NEQ', '');
                                      setFieldValue('companyWebsite', '');

                                      // Nettoie touched/erreurs pour éviter le scroll bloquant
                                      setFieldTouched('companyName', false, false);
                                      setFieldTouched('NEQ', false, false);
                                      setFieldTouched('companyWebsite', false, false);
                                    }
                                  }}
                                  label="Je représente une entreprise"
                                />
                      </Form.Group>

                      {values.hasCompany && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>Nom de l’entreprise *</Form.Label>
                            <Field as={Form.Control} name="companyName"
                              isInvalid={touched.companyName && !!errors.companyName} />
                            <Form.Control.Feedback type="invalid"><ErrorMessage name="companyName" /></Form.Control.Feedback>
                          </Form.Group>

                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>NEQ (10 chiffres) ou « N/A » *</Form.Label>
                                <Field as={Form.Control} name="NEQ" placeholder='1140001234 ou "N/A"'
                                  isInvalid={touched.NEQ && !!errors.NEQ} />
                                <Form.Control.Feedback type="invalid"><ErrorMessage name="NEQ" /></Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Page web</Form.Label>
                                <Field as={Form.Control} name="companyWebsite" placeholder="https://exemple.com"
                                  isInvalid={touched.companyWebsite && !!errors.companyWebsite} />
                                <Form.Control.Feedback type="invalid"><ErrorMessage name="companyWebsite" /></Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </>
                      )}
                    </>
                  )}

                  {/* ====== ÉTAPE 2 : Services ====== */}
                  {step === 1 && (
                    <>
                      <h5 className="mb-3">2. Informations sur vos services</h5>

                      <Form.Group className="mb-3">
                        <Form.Label>Description & spécificités de vos services *</Form.Label>
                        <Field name="servicesDescription">
                          {({ field, meta }) => (
                            <>
                              <Form.Control as="textarea" rows={3} {...field} isInvalid={meta.touched && !!meta.error} />
                              <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                            </>
                          )}
                        </Field>
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Villes couvertes *</Form.Label>
                            <Field as={Form.Control} name="servicedCities" placeholder="Ex: Montréal, Laval, Longueuil"
                              isInvalid={touched.servicedCities && !!errors.servicedCities} />
                            <Form.Control.Feedback type="invalid"><ErrorMessage name="servicedCities" /></Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Frais de service *</Form.Label>
                            <Field as={Form.Control} name="fees" placeholder="Ex: 80$/h"
                              isInvalid={touched.fees && !!errors.fees} />
                            <Form.Control.Feedback type="invalid"><ErrorMessage name="fees" /></Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}

                  {/* ====== Étape 3 ====== */}
                  {step === 2 && (
  <>
    <h5 className="mb-3">3. Informations additionnelles</h5>

    <Row>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Nbr d’années d’expérience *</Form.Label>
          <Field as={Form.Control} type="number" min="0" name="yearsExperience"
            isInvalid={touched.yearsExperience && !!errors.yearsExperience} />
          <Form.Control.Feedback type="invalid"><ErrorMessage name="yearsExperience" /></Form.Control.Feedback>
        </Form.Group>
      </Col>
    </Row>

    <Row>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Où avez-vous entendu parler de nous ? *</Form.Label>
          <Field as={Form.Control} name="heardAboutUs"
                 isInvalid={touched.heardAboutUs && !!errors.heardAboutUs} />
          <Form.Control.Feedback type="invalid">
            <ErrorMessage name="heardAboutUs" />
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Autre commentaire pertinent</Form.Label>
          <Field name="otherComments">
            {({ field, meta }) => (
              <>
                <Form.Control as="textarea" rows={2} {...field}
                              isInvalid={meta.touched && !!meta.error} />
                <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
              </>
            )}
          </Field>
        </Form.Group>
      </Col>
    </Row>

    {/* Langues + niveaux */}
    <div className="mb-3">
      <Form.Label>Langues parlées *</Form.Label>
      <div className="border rounded p-3">
        {LANGUAGES.map((lang) => {
          const checked = values.languagesSelected.includes(lang);
          return (
            <Row key={lang} className="align-items-center mb-2">
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id={`lang-${lang}`}
                  label={lang}
                  checked={checked}
                  onChange={(e) => {
                    const set = new Set(values.languagesSelected);
                    if (e.target.checked) {
                      set.add(lang);
                    } else {
                      set.delete(lang);
                      // retire le niveau si on décoche
                      const nextLevels = { ...(values.languageLevels || {}) };
                      delete nextLevels[lang];
                      setFieldValue('languageLevels', nextLevels, false);
                    }
                    setFieldValue('languagesSelected', Array.from(set));
                  }}
                />
              </Col>

              <Col md={6}>
                {/* Select niveau visible seulement si coché */}
                {checked && (
                  <Form.Select
                    aria-label={`Niveau pour ${lang}`}
                    value={(values.languageLevels && values.languageLevels[lang]) || ''}
                    onChange={(e) => {
                      setFieldValue('languageLevels', {
                        ...(values.languageLevels || {}),
                        [lang]: e.target.value,
                      });
                    }}
                  >
                    <option value="">Choisir un niveau…</option>
                    {LANGUAGE_LEVELS.map(lvl => (
                      <option key={lvl} value={lvl}>{lvl}</option>
                    ))}
                  </Form.Select>
                )}
              </Col>
            </Row>
          );
        })}
      </div>
      {/* erreurs de validation langues */}
      {touched.languagesSelected && errors.languagesSelected && (
        <div className="invalid-feedback d-block">{errors.languagesSelected}</div>
      )}
      {errors.languageLevels && (
        <div className="invalid-feedback d-block">{errors.languageLevels}</div>
      )}
    </div>

    <Form.Group className="mb-3">
      <Form.Label>Date de naissance (optionnel)</Form.Label>
      <Field as={Form.Control} type="date" name="birthDate"
        isInvalid={touched.birthDate && !!errors.birthDate} />
      <Form.Control.Feedback type="invalid"><ErrorMessage name="birthDate" /></Form.Control.Feedback>
    </Form.Group>

    <Form.Group className="mb-3">
      <Form.Label>Appartenance culturelle (optionnel)</Form.Label><br />
      {CULTURES.map(c => (
        <Form.Check
          key={c} type="checkbox" label={c} name="cultures" value={c}
          checked={values.cultures.includes(c)}
          onChange={(e) => {
            const set = new Set(values.cultures);
            e.target.checked ? set.add(c) : set.delete(c);
            setFieldValue('cultures', Array.from(set));
          }}
        />
      ))}
    </Form.Group>
    
    {normalize(values.profession).includes('prepose') && (
  <div className="small text-muted mb-2">
    Pour les préposés, joignez <strong>une licence</strong> ou une <strong>preuve de formation</strong>.
  </div>
)}
    {/* Pièces justificatives */}
    <Form.Group className="mb-3">
      <Form.Label>Pièces justificatives * (CV / autres — PDF/JPG/PNG ≤ 5 Mo)</Form.Label>
      <Form.Control
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={e => setFieldValue('qualifications', e.currentTarget.files)}
        isInvalid={touched.qualifications && !!errors.qualifications}
      />
      <Form.Control.Feedback type="invalid">{errors.qualifications}</Form.Control.Feedback>
      {values.qualifications && values.qualifications.length > 0 && (
        <ul className="small mt-2">
          {Array.from(values.qualifications).map((f, i) => (
            <li key={i}>{f.name} — {(f.size/1024/1024).toFixed(2)} Mo</li>
          ))}
        </ul>
      )}
    </Form.Group>

   {/* Licence / Preuve (au moins un requis pour les préposés) */}
<Row>
  <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label>Licence professionnelle (PDF/JPG/PNG)</Form.Label>
      <Form.Control
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={(e) => setFieldValue('licenseFile', e.currentTarget.files?.[0] || null)}
        isInvalid={touched.licenseFile && !!errors.licenseFile}
      />
      <Form.Control.Feedback type="invalid">
        {errors.licenseFile}
      </Form.Control.Feedback>
      <div className="form-text">Obligatoire si “Préposé” (licence OU preuve).</div>
      {values.licenseFile && (
        <div className="small mt-1">{values.licenseFile.name} — {(values.licenseFile.size/1024/1024).toFixed(2)} Mo</div>
      )}
    </Form.Group>
  </Col>

  <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label>Preuve de formation (PDF/JPG/PNG)</Form.Label>
      <Form.Control
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={(e) => setFieldValue('trainingProofFile', e.currentTarget.files?.[0] || null)}
        isInvalid={touched.trainingProofFile && !!errors.trainingProofFile}
      />
      <Form.Control.Feedback type="invalid">
        {errors.trainingProofFile}
      </Form.Control.Feedback>
      <div className="form-text">Obligatoire si “Préposé” (licence OU preuve).</div>
      {values.trainingProofFile && (
        <div className="small mt-1">{values.trainingProofFile.name} — {(values.trainingProofFile.size/1024/1024).toFixed(2)} Mo</div>
      )}
    </Form.Group>
  </Col>
</Row>
  </>
)}

                  {/* ====== Étape 4 ====== */}
                  {step === 3 && (
                    <>
                      <h5 className="mb-3">4. Consentement & Signature</h5>
                      <Alert variant="light" className="border">
                        En poursuivant, vous acceptez les <strong>Termes & Conditions</strong> de KnowMediQ.
                      </Alert>

                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label="J’accepte de partager mes renseignements personnels de santé avec KnowMediQ afin de recevoir des soins, conformément aux lois canadiennes sur la protection de la vie privée. *"
                          name="consent"
                          checked={values.consent}
                          onChange={e => setFieldValue('consent', e.target.checked)}
                          isInvalid={touched.consent && !!errors.consent}
                        />
                        {touched.consent && errors.consent && <div className="invalid-feedback d-block">{errors.consent}</div>}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Signature (nom complet) *</Form.Label>
                        <Field as={Form.Control} name="signatureFullName" placeholder="Signez en tapant votre nom complet"
                          isInvalid={touched.signatureFullName && !!errors.signatureFullName} />
                        <Form.Control.Feedback type="invalid"><ErrorMessage name="signatureFullName" /></Form.Control.Feedback>
                      </Form.Group>
                    </>
                  )}

                  {/* Navigation */}
                  <div className="d-flex justify-content-between mt-4">
                    <Button type="button" variant="outline-secondary" disabled={step === 0} onClick={back}>
                      Retour
                    </Button>
                    {step < stepsCount - 1 ? (
                      <Button type="button" onClick={() => next(validateForm, setFieldTouched, stepSchemas[step])} className="btn btn-primary">
                        Suivant
                      </Button>
                    ) : (
                      <Button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                        {isSubmitting ? 'Envoi…' : 'Soumettre'}
                      </Button>
                    )}
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        </Col>
      </Row>
    </Container>
  );
}